'use client';

import { c as _c } from "react/compiler-runtime";
import { Divider } from '@common/components';
import { useToast } from '@common/hooks';
import { SvgChevrondown, SvgChevronup, SvgCs, SvgDelete, SvgPadlock } from '@common/icon';
import { cn } from '@core/utils';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { AuthApi } from '@/api';
import { Image } from '@/components/client';
import { ENV } from '@/constants';
import { useAuthStore } from '@/stores/AuthStore';
import { openResponsiveDialog } from '@/stores/DrawerStore';
import { TABS } from '../constants';
import { InquiryModalContent } from './InquiryModalContent';
import { LoginPromptModal } from './LoginPromptModal';
const PAGE_SIZE = 10;
const formatDate = (dateStr?: string) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const yy = String(d.getFullYear()).slice(2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}.${mm}.${dd}`;
};
interface InquirySummary {
  inquiryId: string;
  inquiryType: string;
  inquiryTypeLabel: string;
  title: string;
  secretYN: boolean;
  answerYN: boolean;
  memberNickname: string;
  createAt: string;
}
interface InquiryDetail {
  inquiryId: string;
  inquiryType: string;
  inquiryTypeLabel: string;
  title: string;
  contents: string;
  secretYN: boolean;
  memberNickname: string;
  createAt: string;
  fileUrls: string[] | null;
  answer: string | null;
  answerAt: string | null;
}
interface InquiryListResponse {
  totalCount: number;
  contents: InquirySummary[];
}
const fetchInquiries = async (productId: string, page: number, size: number): Promise<InquiryListResponse> => {
  const res = await fetch(`${ENV.NEXT_PUBLIC_API_DOMAIN}/wello/v2/allows/local-market/products/${productId}/inquiries?page=${page}&size=${size}`);
  const data = await res.json();
  return data.context as InquiryListResponse;
};
const fetchInquiryDetail = async (productId: string, inquiryId: string): Promise<InquiryDetail> => {
  const res = await fetch(`${ENV.NEXT_PUBLIC_API_DOMAIN}/wello/v2/allows/local-market/products/${productId}/inquiries/${inquiryId}`);
  const data = await res.json();
  if (data.return_code !== 0) throw new Error(data.return_message);
  return data.context as InquiryDetail;
};
interface InquiryCardProps {
  inquiry: InquirySummary;
  productId: string;
  onDeleted: () => void;
}
const InquiryCard = ({
  inquiry,
  productId,
  onDeleted
}: InquiryCardProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [detail, setDetail] = useState<InquiryDetail | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const {
    toast
  } = useToast();
  const {
    mutate: deleteInquiry,
    isPending: isDeleting
  } = useMutation({
    mutationFn: () => AuthApi['delete:/wello/v2/auth/local-market/products/{productId}/inquiries/{inquiryId}']({
      path: {
        productId,
        inquiryId: inquiry.inquiryId
      }
    }),
    onSuccess: () => {
      toast({
        message: '문의가 삭제되었습니다.'
      });
      onDeleted();
    },
    onError: () => {
      toast({
        message: '문의 삭제에 실패했습니다.',
        type: 'fail'
      });
    }
  });
  const handleToggle = async () => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    if (inquiry.secretYN && inquiry.title === '비밀글입니다.') {
      toast({
        message: '비밀글은 작성자만 열람 가능합니다.'
      });
      return;
    }
    setIsOpen(true);
    if (!detail) {
      setIsLoadingDetail(true);
      try {
        const data = await fetchInquiryDetail(productId, inquiry.inquiryId);
        setDetail(data);
      } catch {
        toast({
          message: '문의 내용을 불러오지 못했습니다.',
          type: 'fail'
        });
        setIsOpen(false);
      } finally {
        setIsLoadingDetail(false);
      }
    }
  };
  return <div className="border-b border-Gray-200">
      <button type="button" className="flex w-full flex-col gap-8 p-20" onClick={handleToggle}>
        {/* 카테고리 뱃지 */}
        <div className="flex items-center gap-8">
          <span className="rounded-none bg-Gray-200 px-6 py-2 font-600 text-Gray-700 typo-caption1">
            {inquiry.inquiryTypeLabel}
          </span>
        </div>

        {/* 제목 행: 자물쇠 + 제목 */}
        <div className="flex items-center gap-4">
          {inquiry.secretYN && <SvgPadlock className="size-16 shrink-0 text-Gray-500" />}
          <p className="text-left font-500 text-Gray-900 typo-body3">
            {inquiry.title}
          </p>
        </div>

        {/* 날짜 + 답변상태 + chevron */}
        <div className="flex w-full items-center justify-between">
          <span className="font-400 text-Gray-400 typo-caption1">
            {formatDate(inquiry.createAt)}
          </span>
          <div className="flex items-center gap-4">
            {inquiry.answerYN && <span className="font-600 text-Gray-700 typo-caption1">
                답변완료
              </span>}
            {isOpen ? <SvgChevronup className="size-12" /> : <SvgChevrondown className="size-12" />}
          </div>
        </div>
      </button>

      {/* 펼친 내용 */}
      {isOpen && <div className="flex flex-col gap-8 bg-Gray-50 p-20">
          {isLoadingDetail ? <p className="font-400 text-Gray-500 typo-body4">불러오는 중...</p> : detail ? <>
              {/* 문의 내용 */}
              <div className="flex flex-col gap-12 border-b border-Gray-300 pb-16">
                <span className="font-600 text-Gray-700 typo-body4">
                  문의 내용
                </span>
                <p className="whitespace-pre-line font-400 text-Gray-700 typo-body4">
                  {detail.contents}
                </p>
                {detail.fileUrls && detail.fileUrls.length > 0 && <div className="flex flex-wrap gap-8">
                    {detail.fileUrls.map(url => <Image key={url} src={url} alt="첨부 이미지" width={72} height={72} className="size-72 rounded-xsmall border border-Gray-black/5 object-cover" />)}
                  </div>}
              </div>

              {/* 답변 */}
              {detail.answer && <div className="flex flex-col gap-4 pt-12">
                  <span className="font-600 text-Gray-700 typo-body4">
                    답변
                  </span>
                  <p className="whitespace-pre-line font-400 text-Gray-700 typo-body4">
                    {detail.answer}
                  </p>
                  {detail.answerAt && <span className="font-400 text-Gray-400 typo-caption1">
                      {formatDate(detail.answerAt)}
                    </span>}
                </div>}

              {/* 삭제 버튼: 답변 전일 때만 */}
              {!inquiry.answerYN && <div className="flex justify-end pt-8">
                  <button type="button" onClick={() => deleteInquiry()} disabled={isDeleting} className="flex items-center gap-4 font-500 text-Gray-500 typo-caption1">
                    <SvgDelete className="size-14" />
                    삭제
                  </button>
                </div>}
            </> : null}
        </div>}
    </div>;
};
interface TabInquiryProps {
  productId: string;
  productImageUrl?: string;
  vendorRegion?: string;
  vendorName?: string;
  productName?: string;
}
export const TabInquiry = t0 => {
  const $ = _c(62);
  const {
    productId,
    productImageUrl,
    vendorRegion,
    vendorName,
    productName
  } = t0;
  const queryClient = useQueryClient();
  const isLogin = useAuthStore(_temp);
  const [showLoginModal, setShowLoginModal] = useState(false);
  let t1;
  if ($[0] !== productId) {
    t1 = {
      queryKey: ["local-market-inquiries", productId],
      queryFn: t2 => {
        const {
          pageParam: t3
        } = t2;
        const pageParam = t3 === undefined ? 1 : t3;
        return fetchInquiries(productId, pageParam as number, PAGE_SIZE);
      },
      getNextPageParam: _temp3,
      initialPageParam: 1
    };
    $[0] = productId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery(t1);
  const totalCount = data?.pages[0]?.totalCount ?? 0;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== data?.pages || $[3] !== isLogin || $[4] !== productId || $[5] !== productImageUrl || $[6] !== productName || $[7] !== queryClient || $[8] !== totalCount || $[9] !== vendorName || $[10] !== vendorRegion) {
    const allInquiries = data?.pages.flatMap(_temp4) ?? [];
    let t10;
    if ($[19] !== productId || $[20] !== queryClient) {
      t10 = () => {
        queryClient.invalidateQueries({
          queryKey: ["local-market-inquiries", productId]
        });
      };
      $[19] = productId;
      $[20] = queryClient;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    const handleDeleted = t10;
    t9 = TABS.INQUIRY;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = cn("flex flex-col gap-64", "mobile-tablet:gap-48");
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    let t11;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <div className="flex flex-col items-center gap-4 px-20"><p className={cn("text-center font-700 text-Gray-900 typo-body1", "mobile-tablet:typo-body3")}>상품에 대해 궁금한 것이 있으신가요?</p><p className={cn("text-center font-400 text-Gray-600 typo-body2", "mobile-tablet:typo-body4")}>상품 관련 문의는 판매자가 상세히 답변드립니다.</p></div>;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    let t12;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = cn("flex w-full max-w-[375px] items-center justify-center gap-6 border border-Gray-300 bg-Gray-white px-24 py-16 font-700 text-Gray-900 typo-body3", "mobile-tablet:h-48 mobile-tablet:w-200 mobile-tablet:py-8");
      $[24] = t12;
    } else {
      t12 = $[24];
    }
    let t13;
    if ($[25] !== isLogin || $[26] !== productId || $[27] !== productImageUrl || $[28] !== productName || $[29] !== vendorName || $[30] !== vendorRegion) {
      t13 = () => {
        if (!isLogin) {
          setShowLoginModal(true);
          return;
        }
        openResponsiveDialog({
          component: InquiryModalContent,
          modalHeader: "\uC0C1\uD488 \uBB38\uC758\uD558\uAE30",
          className: {
            modal: "min-w-[26rem]"
          },
          drawerType: "full",
          drawerNoPadding: true,
          hideDrawerHeader: true,
          props: {
            productId,
            productImageUrl,
            vendorRegion,
            vendorName,
            productName
          }
        });
      };
      $[25] = isLogin;
      $[26] = productId;
      $[27] = productImageUrl;
      $[28] = productName;
      $[29] = vendorName;
      $[30] = vendorRegion;
      $[31] = t13;
    } else {
      t13 = $[31];
    }
    let t14;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <SvgCs className="size-16" />;
      $[32] = t14;
    } else {
      t14 = $[32];
    }
    if ($[33] !== t13) {
      t8 = <div className="flex flex-col items-center gap-32 mobile-tablet:px-20">{t11}<button type="button" className={t12} onClick={t13}>{t14}상품 문의하기</button></div>;
      $[33] = t13;
      $[34] = t8;
    } else {
      t8 = $[34];
    }
    t5 = "flex flex-col gap-12";
    if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <Divider variant="thick" />;
      $[35] = t6;
    } else {
      t6 = $[35];
    }
    t2 = "flex flex-col";
    if ($[36] !== totalCount) {
      t3 = <div className="px-20 py-16"><h4 className="font-700 text-Gray-900 typo-body2">상품 문의 {totalCount}</h4></div>;
      $[36] = totalCount;
      $[37] = t3;
    } else {
      t3 = $[37];
    }
    t4 = allInquiries.length === 0 ? <div className="flex flex-col items-center justify-center gap-8 py-80"><p className="font-500 text-Gray-600 typo-body3">아직 등록된 문의가 없어요.</p></div> : allInquiries.map(inquiry => <InquiryCard key={inquiry.inquiryId} inquiry={inquiry} productId={productId} onDeleted={handleDeleted} />);
    $[2] = data?.pages;
    $[3] = isLogin;
    $[4] = productId;
    $[5] = productImageUrl;
    $[6] = productName;
    $[7] = queryClient;
    $[8] = totalCount;
    $[9] = vendorName;
    $[10] = vendorRegion;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t2 = $[11];
    t3 = $[12];
    t4 = $[13];
    t5 = $[14];
    t6 = $[15];
    t7 = $[16];
    t8 = $[17];
    t9 = $[18];
  }
  let t10;
  if ($[38] !== t2 || $[39] !== t3 || $[40] !== t4) {
    t10 = <div className={t2}>{t3}{t4}</div>;
    $[38] = t2;
    $[39] = t3;
    $[40] = t4;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  let t11;
  if ($[42] !== fetchNextPage || $[43] !== hasNextPage || $[44] !== isFetchingNextPage) {
    t11 = hasNextPage && <button type="button" className="flex w-full items-center justify-center gap-4 border border-Gray-300 py-12 font-700 text-Gray-900 typo-body3" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>{isFetchingNextPage ? "\uBD88\uB7EC\uC624\uB294 \uC911..." : "\uB354\uBCF4\uAE30"}<SvgChevrondown className="size-16 text-Gray-600" /></button>;
    $[42] = fetchNextPage;
    $[43] = hasNextPage;
    $[44] = isFetchingNextPage;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  let t12;
  if ($[46] !== t10 || $[47] !== t11 || $[48] !== t5 || $[49] !== t6) {
    t12 = <div className={t5}>{t6}{t10}{t11}</div>;
    $[46] = t10;
    $[47] = t11;
    $[48] = t5;
    $[49] = t6;
    $[50] = t12;
  } else {
    t12 = $[50];
  }
  let t13;
  if ($[51] !== t12 || $[52] !== t7 || $[53] !== t8) {
    t13 = <div className={t7}>{t8}{t12}</div>;
    $[51] = t12;
    $[52] = t7;
    $[53] = t8;
    $[54] = t13;
  } else {
    t13 = $[54];
  }
  let t14;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => setShowLoginModal(false);
    $[55] = t14;
  } else {
    t14 = $[55];
  }
  let t15;
  if ($[56] !== showLoginModal) {
    t15 = <LoginPromptModal opened={showLoginModal} onClose={t14} description={"\uB85C\uADF8\uC778 \uD6C4 \uC0C1\uD488 \uBB38\uC758\uAC00 \uAC00\uB2A5\uD574\uC694"} />;
    $[56] = showLoginModal;
    $[57] = t15;
  } else {
    t15 = $[57];
  }
  let t16;
  if ($[58] !== t13 || $[59] !== t15 || $[60] !== t9) {
    t16 = <section id={t9}>{t13}{t15}</section>;
    $[58] = t13;
    $[59] = t15;
    $[60] = t9;
    $[61] = t16;
  } else {
    t16 = $[61];
  }
  return t16;
};
function _temp(state) {
  return state.isLogin;
}
function _temp2(sum, p) {
  return sum + (p?.contents?.length ?? 0);
}
function _temp3(lastPage, allPages) {
  const loaded = allPages.reduce(_temp2, 0);
  return loaded < (lastPage?.totalCount ?? 0) ? allPages.length + 1 : undefined;
}
function _temp4(p_0) {
  return p_0?.contents ?? [];
}